import { useEffect, useState } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import {
  ChartBar, Trophy, CircleNotch, CurrencyDollar, Receipt,
  FilePdf, FileXls, Medal, ChartLineUp,
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const fmtQ = (n) => `Q ${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const MESES_FULL = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const PERIODS = [
  { key: 'day', label: 'Hoy' },
  { key: 'week', label: 'Semana' },
  { key: 'month', label: 'Mes' },
  { key: 'year', label: 'Año' },
];

const Reports = () => {
  const now = new Date();
  const [period, setPeriod] = useState('month');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState({ pdf: false, xls: false });

  const buildParams = () => {
    const p = new URLSearchParams({ period, year, month });
    return p;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sales-report?${buildParams().toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error('Error cargando el reporte de ventas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [period, year, month]);

  const exportFile = async (kind) => {
    setExporting((prev) => ({ ...prev, [kind]: true }));
    try {
      const url = `/sales-report/export-${kind === 'pdf' ? 'pdf' : 'excel'}?${buildParams().toString()}`;
      const res = await api.get(url, { responseType: 'blob' });
      const mime = kind === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const u = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = u;
      a.download = `reporte-ventas-${period}-${year}-${month}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(u);
      toast.success(`${kind.toUpperCase()} descargado`);
    } catch (e) {
      toast.error(`Error exportando ${kind.toUpperCase()}`);
    } finally {
      setExporting((prev) => ({ ...prev, [kind]: false }));
    }
  };

  const totals = data?.totals || {};
  const vendors = data?.by_vendor || [];
  const monthData = (data?.by_month || []).map((m) => ({ ...m, name: MESES[m.month - 1] }));
  const topVendor = vendors[0];

  const periodLabel = {
    day: `Hoy, ${now.toLocaleDateString('es-GT', { day: '2-digit', month: 'long' })}`,
    week: 'Semana en curso',
    month: `${MESES_FULL[month - 1]} ${year}`,
    year: `Año ${year}`,
  }[period];

  return (
    <div className="p-6 max-w-[1400px] mx-auto" style={{ background: 'var(--bg)', color: 'var(--text)' }} data-testid="sales-report-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-faint)] font-bold flex items-center gap-1.5">
            <ChartBar size={11} weight="fill" /> Ventas registradas por vendedores
          </p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight heading-font">Reporte de Ventas</h1>
          <p className="text-xs text-[var(--text-muted)] mt-1 capitalize">{periodLabel}{data?.restricted_to_self ? ' · solo tus ventas' : ''}</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="flex gap-1 bg-[var(--surface)] border border-[var(--border)] rounded-md p-0.5">
            {PERIODS.map((p) => (
              <button key={p.key} onClick={() => setPeriod(p.key)}
                className={`px-3 py-1.5 text-xs font-bold rounded ${period === p.key ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-muted)]'}`}
                data-testid={`period-${p.key}`}>
                {p.label}
              </button>
            ))}
          </div>
          {period === 'month' && (
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
              className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-md px-3 py-2 text-sm" data-testid="filter-month">
              {MESES_FULL.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          )}
          {(period === 'month' || period === 'year') && (
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-md px-3 py-2 text-sm" data-testid="filter-year">
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          )}
          <button onClick={() => exportFile('pdf')} disabled={exporting.pdf}
            className="inline-flex items-center gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white text-xs font-bold px-3 py-2 rounded-md disabled:opacity-50" data-testid="btn-export-pdf">
            {exporting.pdf ? <CircleNotch size={14} className="animate-spin" /> : <FilePdf size={14} weight="bold" />} PDF
          </button>
          <button onClick={() => exportFile('xls')} disabled={exporting.xls}
            className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-2 rounded-md disabled:opacity-50" data-testid="btn-export-excel">
            {exporting.xls ? <CircleNotch size={14} className="animate-spin" /> : <FileXls size={14} weight="bold" />} Excel
          </button>
        </div>
      </div>

      {loading || !data ? (
        <div className="text-center py-16 text-[var(--text-muted)]"><CircleNotch size={28} className="animate-spin inline mb-2" /><p className="text-sm">Cargando...</p></div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <KpiCard icon={CurrencyDollar} label="Total vendido" value={fmtQ(totals.amount)} color="emerald" testId="kpi-total" />
            <KpiCard icon={Receipt} label="Ventas registradas" value={totals.count} color="blue" testId="kpi-count" />
            <KpiCard icon={ChartLineUp} label="Promedio por venta" value={fmtQ(totals.avg)} color="amber" testId="kpi-avg" />
            <KpiCard icon={Trophy} label="Top vendedor" value={topVendor ? topVendor.user_name : '—'} sub={topVendor ? fmtQ(topVendor.amount) : ''} color="purple" testId="kpi-top" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
            {/* Top vendedores */}
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-5 shadow-[var(--shadow-sm)]" data-testid="top-vendors-card">
              <div className="flex items-center gap-2 mb-3">
                <Medal size={20} weight="fill" className="text-amber-500" />
                <h3 className="text-sm font-black heading-font uppercase tracking-wide">Top Vendedores</h3>
                <span className="ml-auto text-[10px] text-[var(--text-faint)] font-bold capitalize">{periodLabel}</span>
              </div>
              {vendors.length === 0 ? (
                <p className="text-center py-8 text-sm text-[var(--text-muted)]">Sin ventas en el período</p>
              ) : (
                <div className="space-y-1.5">
                  {vendors.slice(0, 10).map((v, i) => {
                    const maxAmount = vendors[0]?.amount || 1;
                    const pct = Math.round((v.amount / maxAmount) * 100);
                    const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`;
                    return (
                      <div key={v.user_id || i} className="flex items-center gap-3 py-1.5 border-b border-[var(--border)] last:border-b-0" data-testid={`vendor-rank-${i + 1}`}>
                        <span className="text-base font-black w-8 text-center shrink-0">{medal}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-0.5">
                            <p className="text-sm font-black truncate">{v.user_name}</p>
                            <span className="text-sm font-black text-emerald-600 dark:text-emerald-400">{fmtQ(v.amount)}</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-[var(--bg)] overflow-hidden">
                            <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
                          </div>
                          <p className="text-[10px] text-[var(--text-muted)] mt-1">{v.branch || 'Sin sucursal'} · {v.count} venta{v.count === 1 ? '' : 's'}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Ventas por mes */}
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-5 shadow-[var(--shadow-sm)]" data-testid="monthly-chart-card">
              <div className="flex items-center gap-2 mb-3">
                <ChartBar size={20} weight="fill" className="text-blue-500" />
                <h3 className="text-sm font-black heading-font uppercase tracking-wide">Ventas por Mes · {data.year}</h3>
              </div>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false}
                      tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}k` : v} />
                    <Tooltip
                      formatter={(v, name) => name === 'amount' ? [fmtQ(v), 'Monto'] : [v, 'Ventas']}
                      contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                    />
                    <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                      {monthData.map((m, i) => (
                        <Cell key={i} fill={period === 'month' && m.month === month ? '#10B981' : '#3B82F6'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Detalle de ventas */}
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] overflow-x-auto shadow-[var(--shadow-sm)]" data-testid="sales-detail-table">
            <div className="px-4 pt-4 pb-2 flex items-center gap-2">
              <Receipt size={16} weight="bold" className="text-[var(--text-muted)]" />
              <h3 className="text-sm font-black heading-font uppercase tracking-wide">Detalle de Ventas</h3>
              <span className="ml-auto text-[10px] text-[var(--text-faint)] font-bold">{data.sales.length} registro{data.sales.length === 1 ? '' : 's'}</span>
            </div>
            <table className="w-full text-xs min-w-[700px]">
              <thead className="bg-[var(--surface-muted)] text-[10px] uppercase tracking-wide font-bold text-[var(--text-faint)]">
                <tr>
                  <th className="text-left px-4 py-2">Fecha</th>
                  <th className="text-left px-2 py-2">Vendedor</th>
                  <th className="text-left px-2 py-2">Cliente</th>
                  <th className="text-left px-2 py-2">Sucursal</th>
                  <th className="text-right px-4 py-2">Monto</th>
                </tr>
              </thead>
              <tbody>
                {data.sales.map((s) => (
                  <tr key={s.id} className="border-t border-[var(--border)] hover:bg-[var(--surface-muted)]" data-testid={`sale-row-${s.id}`}>
                    <td className="px-4 py-1.5 text-[var(--text-muted)]">{new Date(s.happened_at).toLocaleString('es-GT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                    <td className="px-2 py-1.5 font-black">{s.user_name}</td>
                    <td className="px-2 py-1.5">{s.customer_name || '—'}</td>
                    <td className="px-2 py-1.5 text-[var(--text-muted)]">{s.branch || '—'}</td>
                    <td className="px-4 py-1.5 text-right font-black text-emerald-600 dark:text-emerald-400">{fmtQ(s.amount)}</td>
                  </tr>
                ))}
                {data.sales.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-8 text-[var(--text-muted)] text-sm">Sin ventas registradas en este período</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, sub, color, testId }) => {
  const map = {
    emerald: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-600 dark:text-emerald-400',
    blue: 'border-blue-500/30 bg-blue-500/5 text-blue-600 dark:text-blue-400',
    amber: 'border-amber-500/30 bg-amber-500/5 text-amber-600 dark:text-amber-400',
    purple: 'border-purple-500/30 bg-purple-500/5 text-purple-600 dark:text-purple-400',
  };
  return (
    <div className={`border rounded-[14px] p-4 ${map[color]}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={14} weight="bold" />
        <p className="text-[10px] uppercase tracking-wide font-bold opacity-80">{label}</p>
      </div>
      <p className="text-2xl font-black text-[var(--text)] truncate">{value}</p>
      {sub && <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{sub}</p>}
    </div>
  );
};

export default Reports;
